import { Link } from "react-router-dom";

/*
 * Blog article: "The Growth Axis: Why Your IGF-1 Range Scales With Your Health"
 * Rendered inside BlogPost.jsx via <article className="legal-prose">.
 */
export default function ArticleBody() {
    return (
        <>
            <h2>Quick answer</h2>
            <p>
                The growth axis is how your body turns growth hormone into
                gains and repair: GH drives IGF-1, and IGF-1 is what
                actually does
                the work. In TRT Plus, the IGF-1 range isn't fixed like
                standard blood panels.
            </p>
            <p>
                Two markers set it. Estradiol, the enabler, determines how
                well your body converts growth hormone into IGF-1. HbA1c,
                the gate, determines how high it runs. Keep both in range
                and your IGF-1 ceiling extends upward. Let either slip and
                it eases down into a more protective band.
            </p>

            <h2>IGF-1 does the work, not growth hormone</h2>
            <p>
                Your growth axis runs as a sequence: growth hormone is
                released in pulses, or injected, and travels to the liver,
                where it is converted into IGF-1, the stable, long-lived
                driver of muscle repair, tissue growth, and recovery.
            </p>
            <p>
                Growth hormone has an extremely short half life and is gone
                within minutes. IGF-1 lasts far longer and acts steadily on
                tissue. So TRT Plus reads IGF-1 as the reliable proxy for
                the entire axis.
            </p>
            <p>
                That leaves one question: how high should your IGF-1 run?
                Two markers answer it, doing two different jobs.
            </p>

            <h2>E2, the enabler</h2>
            <p>
                Estradiol is the enabler: it optimizes the liver's
                conversion of growth hormone into IGF-1. Beyond that
                conversion, estradiol also activates the anabolic signaling
                pathways (insulin/IGF-1 and PI3K/Akt) that increase muscle
                protein synthesis and reduce breakdown.
            </p>
            <p>
                When estradiol is low, both IGF-1 bioavailability and those
                pathways are compromised. Men can see suboptimal muscle
                gains, reduced strength progression, and impaired recovery
                despite adequate testosterone, because their bodies simply
                cannot fully leverage the GH/IGF-1 axis.
            </p>
            <p>
                The practical takeaway: if your IGF-1 is sitting lower than
                you'd expect, estradiol may be the limiting step. It is
                worth fully leveraging E2 (increasing via T) before
                assuming you need to add or increase growth hormone to lift
                your IGF-1.
            </p>

            <h2>HbA1c, the gate</h2>
            <p>
                The main downside of running growth hormone and a high
                IGF-1 is the strain it can place on insulin and blood
                sugar. HbA1c is the clearest read on how well you're
                managing that, your window into long-term blood glucose.
            </p>
            <p>
                So the engine treats good glucose control as permission. If
                your HbA1c shows your glucose is genuinely in check, you've
                demonstrated the metabolic headroom to support a higher
                IGF-1, and your range extends upward. Conversely, if HbA1c
                is trending up, the IGF-1 range narrows and eases down into
                a more protective band.
            </p>
            <p>
                The simple read: adequate estradiol lets your body make
                IGF-1, and good glycemic control is what lets you run it
                higher.
            </p>

            <h2>The Growth Axis</h2>
            <p>
                Your IGF-1 range isn't fixed. It scales with your current
                glycemic control: estradiol enables it, HbA1c gates it,
                IGF-1 reflects it.
            </p>

            <img
                src="/blog/growth-axis.png"
                alt="The TRT Plus biomarker engine, with the growth axis lit: E2 to GH to IGF-1, gated by HbA1c."
                loading="lazy"
                className="block w-full h-auto my-10 rounded-2xl border border-white/10"
            />

            <h2>Frequently asked questions</h2>

            <h3>Why doesn't TRT Plus measure growth hormone?</h3>
            <p>
                Growth hormone has an extremely short half life and is gone
                within minutes, so a blood test can't reliably catch it.
                IGF-1 is the stable, long-lived driver it converts into, so
                the engine reads IGF-1 as the proxy for the whole axis.
            </p>

            <h3>I run GH, but my IGF-1 is still low — why?</h3>
            <p>
                Estradiol may be the limiting step. E2 enables the liver's
                conversion of growth hormone into IGF-1, so when it's low,
                IGF-1 can read low despite a high GH dose. It is worth
                leveraging E2 (via T) before adding more growth hormone.
            </p>

            <h3>Does a low HbA1c help my IGF-1 range?</h3>
            <p>
                Yes. Good glucose control is treated as permission: it
                shows you can better tolerate a higher IGF-1, so your range
                extends upward. HbA1c is assessed on the high side only, so
                good control extends the range while a low reading never
                counts against you.
            </p>

            <div className="hairline my-10" />

            <p className="text-white/50 text-sm italic">
                This article is for informational purposes only and does not
                constitute medical advice. Consult with your healthcare
                provider before making medical decisions.
            </p>

            <h3>See how the engine reads your bloodwork</h3>
            <p>
                The growth axis is one pathway inside the TRT Plus scoring
                engine. <Link to="/engine">See the engine</Link> ·{" "}
                <Link to="/">Get TRT Plus</Link>.
            </p>

            <h3>Related reading</h3>
            <ul>
                <li>
                    <Link to="/blog/how-trt-plus-analyzes-your-bloodwork">
                        How TRT Plus Analyzes Your Bloodwork: A
                        Deterministic Engine, Then AI
                    </Link>
                </li>
                <li>
                    <Link to="/blog/built-here-first">
                        Built Here First: The Ideas TRT Plus Pioneered
                    </Link>
                </li>
            </ul>
        </>
    );
}
